import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type CookieConsent = 'accepted' | 'declined' | 'pending';

const COOKIE_CONSENT_KEY = 'gw2-metas-cookie-consent-v1';

interface CookieState {
  consent: CookieConsent;
  hasConsent: boolean;
  acceptCookies: () => void;
  declineCookies: () => void;
  resetConsent: () => void;
}

const CookieContext = createContext<CookieState | null>(null);

export function CookieProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent>(() => {
    try {
      const saved = localStorage.getItem(COOKIE_CONSENT_KEY) as CookieConsent | null;
      if (saved === 'accepted' || saved === 'declined') {
        return saved;
      }
      return 'pending';
    } catch {
      return 'pending';
    }
  });

  useEffect(() => {
    if (consent !== 'pending') {
      localStorage.setItem(COOKIE_CONSENT_KEY, consent);
    }
  }, [consent]);

  const api = useMemo<CookieState>(() => ({
    consent,
    hasConsent: consent === 'accepted',
    acceptCookies: () => setConsent('accepted'),
    declineCookies: () => setConsent('declined'),
    resetConsent: () => {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      setConsent('pending');
    },
  }), [consent]);

  return <CookieContext.Provider value={api}>{children}</CookieContext.Provider>;
}

export function useCookies() {
  const ctx = useContext(CookieContext);
  if (!ctx) throw new Error('useCookies must be used within CookieProvider');
  return ctx;
}

// Utility functions for cookie management
export const cookieUtils = {
  hasConsent: (): boolean => {
    try {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      return consent === 'accepted';
    } catch {
      return false;
    }
  },
  
  getConsent: (): CookieConsent => {
    try {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY) as CookieConsent | null;
      return consent || 'pending';
    } catch {
      return 'pending';
    }
  },
  
  setConsent: (consent: CookieConsent): void => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, consent);
    } catch {
      console.warn('Failed to save cookie consent');
    }
  },
  
  clearConsent: (): void => {
    try {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
    } catch {
      console.warn('Failed to clear cookie consent');
    }
  }
};
